import React from "react";
import _ from "lodash";

import { ConversationalModule } from "@gen/agency/v1/agency_pb";

declare global {
  interface Window {
    fcWidget?: Record<string, (...args: unknown[]) => void>;
  }
}

type WidgetCall = {
  method: string;
  args: unknown[];
};

type WidgetCallback = (method: string, args: unknown[]) => void;

class Queue {
  data: WidgetCall[];
  index: number;

  constructor() {
    this.data = [];
    this.index = 0;
  }

  queue(value: WidgetCall) {
    this.data.push(value);
  }

  dequeue(): WidgetCall | undefined {
    if (this.index > -1 && this.index < this.data.length) {
      const result = this.data[this.index++];
      if (this.isEmpty) this.reset();
      return result;
    }
  }

  get isEmpty() {
    return this.index >= this.data.length;
  }

  dequeueAll(cb: WidgetCallback) {
    if (!_.isFunction(cb)) {
      throw new Error("Please provide a callback");
    }

    while (!this.isEmpty) {
      const item = this.dequeue();
      if (item) {
        const { method, args } = item;
        cb(method, args);
      }
    }
  }

  reset() {
    this.data.length = 0;
    this.index = 0;
  }
}

const fakeWidget: Record<string, (...args: unknown[]) => void> | null = null;
const earlyCalls = new Queue();

export const widget = (
  fake: Record<string, (...args: unknown[]) => void> | null = fakeWidget,
): Record<string, (...args: unknown[]) => void> => {
  if (window.fcWidget) return window.fcWidget;
  if (!fake) fake = mockMethods(availableMethods);
  return fake;
};

const mockMethods = (
  methods: string[],
): Record<string, (...args: unknown[]) => void> => {
  let obj: Record<string, (...args: unknown[]) => void> = {};
  methods.forEach((method) => {
    obj = _.set(obj, method, queueMethod(method));
  });
  return obj;
};

const queueMethod =
  (method: string) =>
  (...args: unknown[]) => {
    earlyCalls.queue({ method, args });
  };

const loadScript = () => {
  const id = "freshchat-lib";
  if (document.getElementById(id) || window.fcWidget) return;
  const script = document.createElement("script");
  script.async = true;
  script.type = "text/javascript";
  script.src = "https://wchat.eu.freshchat.com/js/widget.js";
  script.id = id;
  document.head.appendChild(script);
};

export interface FreshChatProps {
  conversationalModule: ConversationalModule;
  [key: string]: unknown; // pour moreProps
}

class FreshChat extends React.Component<FreshChatProps> {
  componentDidMount() {
    const { conversationalModule, ...moreProps } = this.props;

    this.init({
      host: conversationalModule?.url,
      token: conversationalModule?.token,
      ...moreProps,
    });
  }

  componentWillUnmount() {
    try {
      widget().destroy?.();
    } catch (e) {
      console.warn("freshchat destroy failed", e);
    }

    const script = document.getElementById("freshchat-lib");
    if (script) script.remove();

    delete window.fcWidget;
    earlyCalls.reset();
  }

  init(settings: Record<string, unknown>) {
    if (settings.onInit && typeof settings.onInit === "function") {
      const tmp = settings.onInit;
      settings.onInit = () => tmp(widget());
    }

    if (window.fcWidget) {
      window.fcWidget.init?.(settings);
      if (settings.onInit && typeof settings.onInit === "function") {
        settings.onInit();
      }
    } else {
      this.lazyInit(settings);
    }
  }

  lazyInit(settings: Record<string, unknown>) {
    widget().init?.(settings);
    loadScript();

    const interval = setInterval(() => {
      if (window.fcWidget) {
        clearInterval(interval);
        try {
          earlyCalls.dequeueAll((method: string, args: unknown[]) => {
            window.fcWidget?.[method]?.(...args);
          });
        } catch (e) {
          console.error(e);
        }

        if (settings.onInit && typeof settings.onInit === "function") {
          settings.onInit();
        }
      }
    }, 1000);
  }

  render() {
    return false;
  }
}

const availableMethods = [
  "close",
  "destroy",
  "hide",
  "init",
  "isInitialized",
  "isLoaded",
  "isOpen",
  "off",
  "on",
  "open",
  "setConfig",
  "setExternalId",
  "setFaqTags",
  "setTags",
  "track",
  "user.show",
  "user.track",
  "user.user",
  "user.clear",
  "user.create",
  "user.get",
  "user.isExists",
  "user.setEmail",
  "user.setFirstName",
  "user.setLastName",
  "user.setMeta",
  "user.setPhone",
  "user.setPhoneCountryCode",
  "user.setProperties",
  "user.update",
];

export default FreshChat;
